import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity,
  Cpu,
  Users,
  ListOrdered,
  HardDrive,
  RefreshCw,
  Loader2,
  Workflow,
  Zap,
  Database,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface MSSQLSchedulersViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-200 text-[10px] truncate max-w-[150px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const MSSQLSchedulersView: React.FC<MSSQLSchedulersViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [schedulersData, setSchedulersData] = useState<any>(null);

  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : true
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Hover states for legends
  const [hoveredWorkers, setHoveredWorkers] = useState<string | null>(null);
  const [hoveredTasks, setHoveredTasks] = useState<string | null>(null);
  const [hoveredRunnable, setHoveredRunnable] = useState<string | null>(null);
  const [hoveredQueue, setHoveredQueue] = useState<string | null>(null);
  const [hoveredSwitches, setHoveredSwitches] = useState<string | null>(null);
  const [hoveredPendingIO, setHoveredPendingIO] = useState<string | null>(null);
  const [hoveredTaskIO, setHoveredTaskIO] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      
      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/schedulers', { params });
      setSchedulersData(res.data?.data || res.data);
    } catch (err) {
      console.warn('MSSQLSchedulersView fetch error:', err);
      setSchedulersData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels: string[] = schedulersData?.labels || [];
  const series: Record<string, number[]> = schedulersData?.series || {};

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = formatTimeLabels(labels);

  const formattedData = labels.map((_: string, idx: number) => {
    const currentTasks = Number(series.current_tasks?.[idx] ?? 0);
    const runnableTasks = Number(series.runnable_tasks?.[idx] ?? 0);
    const currentWorkers = Number(series.current_workers?.[idx] ?? 0);
    const activeWorkers = Number(series.active_workers?.[idx] ?? 0);
    const workQueueCount = Number(series.work_queue_count?.[idx] ?? 0);
    const pendingDiskIO = Number(series.pending_disk_io?.[idx] ?? 0);
    const contextSwitches = Number(series.context_switches?.[idx] ?? 0);
    const rawTaskIO = Number(series.task_io_bytes?.[idx] ?? 0);

    const taskIOMB = Number((rawTaskIO / (1024 * 1024)).toFixed(2));

    return {
      time: timeLabels[idx],
      current_tasks: Number(currentTasks.toFixed(2)),
      runnable_tasks: Number(runnableTasks.toFixed(2)),
      current_workers: Number(currentWorkers.toFixed(2)),
      active_workers: Number(activeWorkers.toFixed(2)),
      work_queue_count: Number(workQueueCount.toFixed(2)),
      pending_disk_io: Number(pendingDiskIO.toFixed(2)),
      context_switches: Number(contextSwitches.toFixed(2)),
      task_io_bytes: rawTaskIO,
      task_io_mb: taskIOMB,
    };
  });

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) =>
      Object.keys(item).some(
        (k) => k !== 'time' && typeof item[k] === 'number' && item[k] > 0
      )
    );
  };

  const summary = schedulersData?.summary?.latest || {};

  const formatNumber = (num: number | undefined | null, decimals = 2) => {
    if (num === undefined || num === null || isNaN(num)) return '0';
    const val = Number(num);
    return Number.isInteger(val)
      ? val.toLocaleString()
      : val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
  };

  const formatBytes = (bytes: number | undefined | null) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return '0 B';
    const b = Number(bytes);
    if (b === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(Math.abs(b)) / Math.log(k));
    const idx = Math.min(i, sizes.length - 1);
    return `${parseFloat((b / Math.pow(k, idx)).toFixed(2))} ${sizes[idx]}`;
  };

  const getLatestVal = useCallback((key: string) => {
    if (summary[key] !== undefined && summary[key] !== null) {
      return Number(summary[key]);
    }
    const arr = series[key];
    if (Array.isArray(arr) && arr.length > 0) {
      const last = arr[arr.length - 1];
      return last !== undefined && last !== null ? Number(last) : 0;
    }
    return 0;
  }, [summary, series]);

  const summaryCards = useMemo(() => {
    const currentTasksVal = getLatestVal('current_tasks');
    const runnableTasksVal = getLatestVal('runnable_tasks');
    const currentWorkersVal = getLatestVal('current_workers');
    const activeWorkersVal = getLatestVal('active_workers');
    const workQueueCountVal = getLatestVal('work_queue_count');
    const pendingDiskIOVal = getLatestVal('pending_disk_io');
    const contextSwitchesVal = getLatestVal('context_switches');
    const taskIOBytesVal = getLatestVal('task_io_bytes');

    return [
      {
        label: 'Current Tasks',
        value: formatNumber(currentTasksVal),
        icon: Workflow,
        cardBg: 'from-blue-50 to-white dark:from-blue-950/20 dark:to-gray-900/80 border-blue-200 dark:border-blue-500/30',
        color: 'text-blue-600 dark:text-blue-400',
      },
      {
        label: 'Runnable Tasks',
        value: formatNumber(runnableTasksVal),
        icon: Cpu,
        cardBg: 'from-amber-50 to-white dark:from-amber-950/20 dark:to-gray-900/80 border-amber-200 dark:border-amber-500/30',
        color: 'text-amber-600 dark:text-amber-400',
      },
      {
        label: 'Current Workers',
        value: formatNumber(currentWorkersVal),
        icon: Users,
        cardBg: 'from-emerald-50 to-white dark:from-emerald-950/20 dark:to-gray-900/80 border-emerald-200 dark:border-emerald-500/30',
        color: 'text-emerald-600 dark:text-emerald-400',
      },
      {
        label: 'Active Workers',
        value: formatNumber(activeWorkersVal),
        icon: Zap,
        cardBg: 'from-purple-50 to-white dark:from-purple-950/20 dark:to-gray-900/80 border-purple-200 dark:border-purple-500/30',
        color: 'text-purple-600 dark:text-purple-400',
      },
      {
        label: 'Work Queue Count',
        value: formatNumber(workQueueCountVal),
        icon: ListOrdered,
        cardBg: 'from-rose-50 to-white dark:from-rose-950/20 dark:to-gray-900/80 border-rose-200 dark:border-rose-500/30',
        color: 'text-rose-600 dark:text-rose-400',
      },
      {
        label: 'Pending Disk I/O',
        value: formatNumber(pendingDiskIOVal),
        icon: HardDrive,
        cardBg: 'from-cyan-50 to-white dark:from-cyan-950/20 dark:to-gray-900/80 border-cyan-200 dark:border-cyan-500/30',
        color: 'text-cyan-600 dark:text-cyan-400',
      },
      {
        label: 'Context Switches',
        value: formatNumber(contextSwitchesVal),
        icon: RefreshCw,
        cardBg: 'from-indigo-50 to-white dark:from-indigo-950/20 dark:to-gray-900/80 border-indigo-200 dark:border-indigo-500/30',
        color: 'text-indigo-600 dark:text-indigo-400',
      },
      {
        label: 'Task I/O',
        value: formatBytes(taskIOBytesVal),
        icon: Database,
        cardBg: 'from-teal-50 to-white dark:from-teal-950/20 dark:to-gray-900/80 border-teal-200 dark:border-teal-500/30',
        color: 'text-teal-600 dark:text-teal-400',
      },
    ];
  }, [getLatestVal]);

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.15;
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <Activity className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Schedulers</h2>
        </div>
      </div>

      {/* Top Summary KPI Cards - 4 cards per row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-2.5">
        {summaryCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div
              key={idx}
              className={`bg-gradient-to-br ${card.cardBg} border rounded-xl p-3 shadow-sm dark:shadow-md flex flex-col justify-between`}
            >
              <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-200 font-semibold mb-1">
                <span>{card.label}</span>
                <Icon className={`w-3.5 h-3.5 ${card.color}`} />
              </div>
              <div className={`text-base font-bold font-mono ${card.color}`}>
                {card.value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Row 1: Tasks & Runnable Tasks (CPU Scheduler Pressure) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        {/* Graph 1: Current vs Runnable Tasks */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <Workflow className="w-4 h-4 text-blue-500 dark:text-blue-400" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Current vs Runnable Tasks
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorCurrentTasks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorRunnableTasks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="Tasks" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredTasks}
                        onHoverSeries={setHoveredTasks}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="current_tasks"
                    name="Current Tasks"
                    stroke="#3b82f6"
                    strokeOpacity={getOpacity(hoveredTasks, 'current_tasks')}
                    fillOpacity={getOpacity(hoveredTasks, 'current_tasks') * 0.4}
                    fill="url(#colorCurrentTasks)"
                    strokeWidth={hoveredTasks === 'current_tasks' ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="runnable_tasks"
                    name="Runnable Tasks"
                    stroke="#f59e0b"
                    strokeOpacity={getOpacity(hoveredTasks, 'runnable_tasks')}
                    fillOpacity={getOpacity(hoveredTasks, 'runnable_tasks') * 0.4}
                    fill="url(#colorRunnableTasks)"
                    strokeWidth={hoveredTasks === 'runnable_tasks' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No tasks telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 2: Worker Threads (Current vs Active Workers) */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <Users className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Worker Threads (Current vs Active)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorCurrWorkers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorActiveWorkers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="Workers" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredWorkers}
                        onHoverSeries={setHoveredWorkers}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="current_workers"
                    name="Current Workers"
                    stroke="#10b981"
                    strokeOpacity={getOpacity(hoveredWorkers, 'current_workers')}
                    fillOpacity={getOpacity(hoveredWorkers, 'current_workers') * 0.4}
                    fill="url(#colorCurrWorkers)"
                    strokeWidth={hoveredWorkers === 'current_workers' ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="active_workers"
                    name="Active Workers"
                    stroke="#8b5cf6"
                    strokeOpacity={getOpacity(hoveredWorkers, 'active_workers')}
                    fillOpacity={getOpacity(hoveredWorkers, 'active_workers') * 0.4}
                    fill="url(#colorActiveWorkers)"
                    strokeWidth={hoveredWorkers === 'active_workers' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No worker telemetry available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: Context Switches & Runnable Tasks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        {/* Graph 3: Context Switches */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <RefreshCw className="w-4 h-4 text-purple-500 dark:text-purple-400" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Context Switches
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="Switches" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredSwitches}
                        onHoverSeries={setHoveredSwitches}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="context_switches"
                    name="Context Switches"
                    stroke="#a855f7"
                    strokeOpacity={getOpacity(hoveredSwitches, 'context_switches')}
                    strokeWidth={hoveredSwitches === 'context_switches' ? 2.5 : 1.5}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No context switches telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 4: Runnable Tasks (CPU Queue Indicator) */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <Cpu className="w-4 h-4 text-amber-500 dark:text-amber-400" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Runnable Tasks (Queue Length)
            </h4>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorRunTasksOnly" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={4}>
                    <Label value="Tasks" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredRunnable}
                        onHoverSeries={setHoveredRunnable}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="runnable_tasks"
                    name="Runnable Tasks"
                    stroke="#f59e0b"
                    strokeOpacity={getOpacity(hoveredRunnable, 'runnable_tasks')}
                    fillOpacity={getOpacity(hoveredRunnable, 'runnable_tasks') * 0.4}
                    fill="url(#colorRunTasksOnly)"
                    strokeWidth={hoveredRunnable === 'runnable_tasks' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No runnable task queue telemetry available</span>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Work Queue Count, Pending Disk I/O & Task I/O */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        {/* Graph 5: Work Queue Count */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <ListOrdered className="w-4 h-4 text-rose-500 dark:text-rose-400" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Work Queue Count
            </h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorWorkQueue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={3}>
                    <Label value="Count" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredQueue}
                        onHoverSeries={setHoveredQueue}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="work_queue_count"
                    name="Work Queue"
                    stroke="#f43f5e"
                    strokeOpacity={getOpacity(hoveredQueue, 'work_queue_count')}
                    fillOpacity={getOpacity(hoveredQueue, 'work_queue_count') * 0.4}
                    fill="url(#colorWorkQueue)"
                    strokeWidth={hoveredQueue === 'work_queue_count' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No work queue telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 6: Pending Disk I/O */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <HardDrive className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Pending Disk I/O
            </h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorPendingIO" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={3}>
                    <Label value="I/O" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredPendingIO}
                        onHoverSeries={setHoveredPendingIO}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="pending_disk_io"
                    name="Pending Disk I/O"
                    stroke="#06b6d4"
                    strokeOpacity={getOpacity(hoveredPendingIO, 'pending_disk_io')}
                    fillOpacity={getOpacity(hoveredPendingIO, 'pending_disk_io') * 0.4}
                    fill="url(#colorPendingIO)"
                    strokeWidth={hoveredPendingIO === 'pending_disk_io' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No pending I/O telemetry available</span>
            )}
          </div>
        </div>

        {/* Graph 7: Task I/O Bytes */}
        <div>
          <div className="flex items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <Cpu className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
            <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
              Task I/O
            </h4>
          </div>
          <div className="h-40 w-full flex items-center justify-center">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="colorTaskIO" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={isDark ? 0.2 : 0.7} />
                  <XAxis dataKey="time" stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} />
                  <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} tickLine={false} tickCount={3}>
                    <Label value="Bytes" angle={-90} position="insideLeft" offset={10} fill={isDark ? '#9ca3af' : '#6b7280'} fontSize={10} />
                  </YAxis>
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    contentStyle={{
                      backgroundColor: isDark ? '#111827' : '#ffffff',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      color: isDark ? '#f3f4f6' : '#111827',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                  />
                  <Legend
                    content={
                      <ScrollableLegend
                        hoveredSeries={hoveredTaskIO}
                        onHoverSeries={setHoveredTaskIO}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="task_io_bytes"
                    name="Task I/O Bytes"
                    stroke="#10b981"
                    strokeOpacity={getOpacity(hoveredTaskIO, 'task_io_bytes')}
                    fillOpacity={getOpacity(hoveredTaskIO, 'task_io_bytes') * 0.4}
                    fill="url(#colorTaskIO)"
                    strokeWidth={hoveredTaskIO === 'task_io_bytes' ? 2.5 : 1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No task I/O telemetry available</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MSSQLSchedulersView;

